import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';

/**
 * The tills in one store.
 *
 * A *lane* is a register: a tag the staff know it by, and whatever the app
 * running on it last reported about itself — its version, the hardware, when
 * it last took money. Blocking one stops it trading without deleting it.
 *
 * The list answers `{ latestVersion, lanes }`, not a bare array: the version
 * every lane *should* be on travels with the list so a lane behind it can be
 * spotted without a second call.
 */

const get = <T>(url: string) => unwrap<T>(api.get<ApiEnvelope<T>>(url));

/** `PosDto`. */
export interface PosLane {
  id: string;
  businessId: string | null;
  businessName: string | null;
  tag: string | null;
  isBlocked: boolean;
  createdAt: string | null;
  lastReportedVersion: string | null;
  lastReportedDeviceManufacturer: string | null;
  lastReportedDeviceModel: string | null;
  lastReportedDeviceName: string | null;
  lastReportedTerminalId: string | null;
  lastTransactionOn: string | null;
}

/** `PosLanesResultModel`. */
export interface PosLanes {
  latestVersion: string | null;
  lanes: PosLane[] | null;
}

export function usePosLanes(businessId: string | null) {
  return useQuery({
    queryKey: ['pos-lanes', businessId],
    queryFn: () => get<PosLanes>(`pos/manage/v2/${businessId}`),
    enabled: !!businessId,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['pos-lanes'] });
}

/** `CreatePosBindingModel` — a lane starts as nothing but a tag. */
export function useCreatePosLane() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ businessId, tag }: { businessId: string; tag: string }) =>
      unwrap<unknown>(api.post('pos/manage', { businessId, tag: tag.trim() })),
    onSuccess: invalidate,
  });
}

/** `UpdatePosBindingModel` — the id rides in the body, not the path. */
export function useUpdatePosLane() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      id,
      tag,
      isBlocked,
    }: {
      id: string;
      tag: string;
      isBlocked: boolean;
    }) => unwrap<unknown>(api.put('pos/manage', { id, tag: tag.trim(), isBlocked })),
    onSuccess: invalidate,
  });
}

export function useDeletePosLane() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) => unwrap<unknown>(api.delete(`pos/manage/${id}`)),
    onSuccess: invalidate,
  });
}
